import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Button, Card, Callout, Spinner, Badge, TextField, Select, Table, type Column, type SelectOption } from "@/design/components";
import { ApiError } from "@/api/client";
import { listOperatorOrgs, type OperatorOrgSummary, type OrgStatus, type OrgTier, type SubscriptionFilter } from "@/api/operator/orgs";
import styles from "./OperatorOrgsScreen.module.css";

const PAGE = 25;
const STATUS_OPTIONS: SelectOption[] = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "deleted", label: "Deleted" },
];
const TIER_OPTIONS: SelectOption[] = [
  { value: "", label: "Any tier" },
  { value: "free", label: "Free" },
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "xlarge", label: "XLarge" },
];
const SUB_OPTIONS: SelectOption[] = [
  { value: "", label: "Any subscription" },
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "past_due", label: "Past due" },
  { value: "canceled", label: "Canceled" },
  { value: "none", label: "No subscription" },
];
const apiMessage = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message : fallback);
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
const STATUS_TONE: Record<OrgStatus, "success" | "warning" | "danger"> = {
  active: "success",
  suspended: "warning",
  deleted: "danger",
};

/** Cross-tenant org directory: name search (debounced) + keyset "Load more". */
export function OperatorOrgsScreen() {
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tier, setTier] = useState("");
  const [subscription, setSubscription] = useState("");
  // Debounce the search so each keystroke doesn't refetch.
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const query = useInfiniteQuery({
    queryKey: ["operator", "orgs", q, status, tier, subscription],
    queryFn: ({ pageParam, signal }) =>
      listOperatorOrgs(
        {
          q: q || undefined,
          status: (status || undefined) as OrgStatus | undefined,
          tier: (tier || undefined) as OrgTier | undefined,
          subscription: (subscription || undefined) as SubscriptionFilter | undefined,
          before: pageParam,
          limit: PAGE,
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
  const hasFilters = q !== "" || status !== "" || tier !== "" || subscription !== "";
  const orgs = useMemo(() => query.data?.pages.flatMap((p) => p.orgs) ?? [], [query.data]);
  const hasLoaded = query.data !== undefined;

  const columns: Column<OperatorOrgSummary>[] = [
    { key: "name", header: "Organization", render: (o) => <Link className={styles.name} to={`/operator/app/orgs/${o.id}`}>{o.name}</Link> },
    { key: "tier", header: "Tier", render: (o) => <span className={styles.mono}>{o.tier}</span> },
    { key: "status", header: "Status", render: (o) => <Badge tone={STATUS_TONE[o.status]}>{o.status}</Badge> },
    { key: "sub", header: "Subscription", render: (o) => <span>{o.subscriptionStatus ?? "—"}</span> },
    { key: "channels", header: "Channels", align: "end", render: (o) => <span className={styles.mono}>{o.channels}</span> },
    { key: "members", header: "Members", align: "end", render: (o) => <span className={styles.mono}>{o.members}</span> },
    { key: "created", header: "Created", render: (o) => <span className={styles.muted}>{formatDate(o.createdAt)}</span> },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Platform</p>
          <h1 className={styles.h1}>Organizations</h1>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <TextField label="Search" placeholder="Name, admin email, scope, Stripe id…" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </div>
        <div className={styles.filter}>
          <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={(e) => setStatus(e.target.value)} />
        </div>
        <div className={styles.filter}>
          <Select label="Tier" options={TIER_OPTIONS} value={tier} onChange={(e) => setTier(e.target.value)} />
        </div>
        <div className={styles.filter}>
          <Select label="Subscription" options={SUB_OPTIONS} value={subscription} onChange={(e) => setSubscription(e.target.value)} />
        </div>
      </div>

      {query.isPending && (
        <div className={styles.loading}>
          <Spinner size={20} color="var(--accent)" /> <span>Loading organizations…</span>
        </div>
      )}
      {query.isError && (
        <Callout tone="danger" title="Couldn't load organizations">{apiMessage(query.error, "Please try again shortly.")}</Callout>
      )}
      {hasLoaded && !query.isError && (
        <Card className={styles.tableCard}>
          <Table
            columns={columns}
            rows={orgs}
            getRowKey={(o) => o.id}
            emptyText={hasFilters ? "No organizations match your filters." : "No organizations yet."}
          />
        </Card>
      )}
      {query.hasNextPage && (
        <div className={styles.loadMore}>
          <Button variant="secondary" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
